import { create } from 'zustand'
import type { ApiItemDetail } from '@/models/Item'
import i18n from '@/lib/i18n'

type AppState = {
  updateState: <T>(key: string, value: T) => void
  loaded?: boolean
  userLoaded?: boolean
  delta?: number
  errors?: { [key: string]: string }
  loginSuccess?: boolean
  params?: { [key: string]: string }[]
  modalRuleStatus?: boolean
  modalExchangeStatus?: boolean
  modalHistoryStatus?: boolean
  stateLoaded?: boolean
  isAniming?: boolean
  translation: Record<string, { [key: string]: string }>
  language: string
  setLanguage: (lang: string) => void
  setTranslation: (lang: string, data: Record<string, string>) => void
  // Free Fire Companion states
  searchModalOpen?: boolean
  selectedItem?: ApiItemDetail | null
  favorites?: number[]
  profileModalOpen?: boolean
  
  // Page filter states
  pageGenreFilter: number | null
  pageWeaponFilter: number | null
  pageTagsFilter: number[]
  pagePowerTypeFilter: boolean
  isIngameBrowser: boolean
  
  // Gun comparison states
  compareList: ApiItemDetail[]
  compareModalOpen: boolean
  addToCompare: (item: ApiItemDetail) => void
  removeFromCompare: (itemId: number) => void
  clearCompareList: () => void
  setCompareModalOpen: (open: boolean) => void
  isInCompareList: (itemId: number) => boolean
  replaceInCompareList: (index: number, item: ApiItemDetail) => void
  
  getParam: (key: string) => string
  getError: (error: any) => string
}

export const useStore = create<AppState>((set, get) => ({
  loaded: false,
  userLoaded: false,
  delta: 0,
  errors: {},
  params: [],
  loginSuccess: false,
  modalRuleStatus: false,
  modalExchangeStatus: false,
  modalHistoryStatus: false,
  stateLoaded: false,
  isAniming: false,
  gameState: null,
  isIngameBrowser: false,
  translation: {
    vi: {},
    en: {},
  },
  language: 'vi',
  // Free Fire Companion initial states
  searchModalOpen: false,
  selectedItem: null,
  favorites: [],
  profileModalOpen: false,
  
  // Page filter initial states
  pageGenreFilter: null,
  pageWeaponFilter: null,
  pageTagsFilter: [],
  pagePowerTypeFilter: true,
  
  // Gun comparison initial states
  compareList: [],
  compareModalOpen: false,
  
  // Gun comparison actions
  addToCompare: (item: ApiItemDetail) => {
    const { compareList } = get();
    if (compareList.length >= 3) return; // Max 3 items
    if (compareList.some(i => i.id === item.id)) return; // Already in list
    set({ compareList: [...compareList, item] });
  },
  
  removeFromCompare: (itemId: number) => {
    const { compareList } = get();
    set({ compareList: compareList.filter(i => i.id !== itemId) });
  },
  
  clearCompareList: () => {
    set({ compareList: [], compareModalOpen: false });
  },
  
  setCompareModalOpen: (open: boolean) => {
    set({ compareModalOpen: open });
  },
  
  isInCompareList: (itemId: number) => {
    const { compareList } = get();
    return compareList.some(i => i.id === itemId);
  },
  
  replaceInCompareList: (index: number, item: ApiItemDetail) => {
    const { compareList } = get();
    const newList = [...compareList];
    if (index >= 0 && index < newList.length) {
      newList[index] = item;
      set({ compareList: newList });
    }
  },
  
  setLanguage: (lang: string) => {
    set({ language: lang });
    i18n.changeLanguage(lang);
  },
  
  setTranslation: (lang: string, data: Record<string, string>) => {
    const { translation } = get();
    set({
      translation: {
        ...translation,
        [lang]: { ...translation[lang], ...data },
      },
    });
    i18n.addResourceBundle(lang, 'translation', data, true, true);
  },
  
  getParam: (key: string) => {
    const params = get().params
    return params?.[key as any] as any
  },
  getError: (error: any) => {
    const errors = get().errors
    const key = (error?.code || error?.message) as string
    return errors?.[key] || `Hệ thống chưa thể thực hiện thao tác của bạn: ${key}`
  },
  updateState: <T>(key: string, value: T) => set(() => ({
    [key]: value
  }))
} as any as AppState))